/**
 * ResetPasswordPage — consumes the one-time token from the email link and lets
 * the user set a new password. On success, user is redirected to /login.
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { TrendingUp, Lock, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { t } = useLanguage();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) { setError(t.rp_min_len || "Password must be at least 6 characters."); return; }
    if (pwd !== pwd2) { setError(t.rp_mismatch || "Passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4" data-testid="reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-black font-['Chivo'] text-white">TopRankPulse</span>
          </Link>
        </div>

        <div className="bg-gray-900 border border-gray-800 p-8">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-white mb-2 font-['Chivo']">
                {t.rp_done_title || "Password updated"}
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                {t.rp_done_sub || "You can now sign in with your new password."}
              </p>
              <Link to="/login" data-testid="reset-goto-login"
                className="inline-block w-full py-3 bg-blue-600 text-white font-bold hover:bg-blue-500 transition-colors">
                {t.rp_goto_login || "Sign in"}
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-1 font-['Chivo']" data-testid="reset-title">
                {t.rp_title || "Choose a new password"}
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                {t.rp_sub || "Pick something you don't use on any other site."}
              </p>
              <form onSubmit={submit} className="space-y-4">
                <PasswordField value={pwd} onChange={setPwd} show={showPwd} setShow={setShowPwd}
                  testid="reset-pwd-input" label={t.rp_new_pwd || "New password"} />
                <PasswordField value={pwd2} onChange={setPwd2} show={showPwd} setShow={setShowPwd}
                  testid="reset-pwd-confirm" label={t.rp_confirm || "Confirm new password"} />
                {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 px-3 py-2" data-testid="reset-error">{error}</div>}
                <button type="submit" disabled={loading || !pwd || !pwd2} data-testid="reset-submit"
                  className="w-full py-3 bg-blue-600 text-white font-bold hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t.rp_submit || "Reset password"}
                </button>
              </form>
              <p className="text-center text-gray-500 text-xs mt-6">
                <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
                  {t.auth_back || "Back to sign in"}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordField({ value, onChange, show, setShow, label, testid }) {
  return (
    <div>
      <label className="block text-sm text-gray-400 mb-1">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input data-testid={testid} type={show ? "text" : "password"}
          value={value} onChange={(e) => onChange(e.target.value)} required minLength={6}
          className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none" />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
